import React, { useState, useEffect } from "react";
import { Form, Button, Container, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "./AddPurchase.css";

const AddOD = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    id: "",
    title: "",
    description: "",
    assigned_to: "",
    assigned_by: "",
    priority: "",
    due_date: "",
    location: { lat: "", lng: "" }, // store as object to match list view
    status: "",
    created_at: "",
    updated_at: "",
  });

  const [errors, setErrors] = useState({});
  const [locLoading, setLocLoading] = useState(false);

  useEffect(() => {
    const now = new Date().toISOString().slice(0, 16); // YYYY-MM-DDTHH:mm
    setFormData((prev) => ({
      ...prev,
      created_at: now,
      updated_at: now,
    }));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // handle nested location fields (latitude/longitude)
    if (name === "lat" || name === "lng") {
      setFormData((prev) => ({
        ...prev,
        location: { ...prev.location, [name === "lat" ? "lat" : "lng"]: value },
        updated_at: new Date().toISOString().slice(0, 16),
      }));
      return;
    }

    // other top-level fields
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      updated_at: new Date().toISOString().slice(0, 16),
    }));
  };

  const validate = () => {
    const v = {};
    if (!formData.title.trim()) v.title = "Title is required";
    if (!formData.assigned_to.trim()) v.assigned_to = "Assigned To is required";
    if (!formData.assigned_by.trim()) v.assigned_by = "Assigned By is required";
    if (!formData.priority) v.priority = "Select priority";
    if (!formData.due_date) v.due_date = "Select due date";
    if (!formData.status) v.status = "Select status";
    // location may be optional; if provided, validate numbers
    const lat = formData.location?.lat;
    const lng = formData.location?.lng;
    if ((lat && isNaN(Number(lat))) || (lng && isNaN(Number(lng)))) {
      v.location = "Latitude and longitude must be valid numbers";
    }
    return v;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length) {
      setErrors(validationErrors);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setErrors({});
    // prepare payload: convert lat/lng to numbers if present
    const payload = {
      ...formData,
      location:
        formData.location && formData.location.lat !== ""
          ? { lat: Number(formData.location.lat), lng: Number(formData.location.lng) }
          : "",
      // keep created_at/updated_at format as datetime-local string (already set)
    };

    console.log("OD Submitted:", payload);
    // TODO: send payload to backend or save to localStorage
    navigate("/odlist");
  };

  const fillCurrentLocation = () => {
    if (!navigator.geolocation) {
      setErrors({ ...errors, location: "Geolocation not supported by browser" });
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const lat = pos.coords.latitude.toFixed(6);
        const lng = pos.coords.longitude.toFixed(6);
        setFormData((prev) => ({
          ...prev,
          location: { lat, lng },
          updated_at: new Date().toISOString().slice(0, 16),
        }));
        setErrors((prev) => {
          const copy = { ...prev };
          delete copy.location;
          return copy;
        });
        setLocLoading(false);
      },
      () => {
        setErrors({ ...errors, location: "Unable to fetch location" });
        setLocLoading(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const renderError = (field) =>
    errors[field] ? <div className="text-danger small mt-1">{errors[field]}</div> : null;

  return (
    <Container className="my-4">
      <div className="form-header mb-4 d-flex justify-content-between align-items-center">
        <h4 className="mb-0">Add OD</h4>
        <Button variant="secondary" onClick={() => navigate(-1)}>Back</Button>
      </div>

      <Form onSubmit={handleSubmit} noValidate>
        <Row className="g-3">
          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Title</Form.Label>
              <Form.Control
                name="title"
                type="text"
                value={formData.title}
                onChange={handleChange}
                placeholder="OD title"
              />
              {renderError("title")}
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Assigned To</Form.Label>
              <Form.Control
                name="assigned_to"
                type="text"
                value={formData.assigned_to}
                onChange={handleChange}
                placeholder="Recipient email or name"
              />
              {renderError("assigned_to")}
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Assigned By</Form.Label>
              <Form.Control
                name="assigned_by"
                type="text"
                value={formData.assigned_by}
                onChange={handleChange}
                placeholder="Issuer email or name"
              />
              {renderError("assigned_by")}
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Priority</Form.Label>
              <Form.Select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="custom-select-arrow"
              >
                <option value="">Select Priority</option>
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </Form.Select>
              {renderError("priority")}
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Due Date</Form.Label>
              <Form.Control
                name="due_date"
                type="date"
                value={formData.due_date}
                onChange={handleChange}
              />
              {renderError("due_date")}
            </Form.Group>
          </Col>

          <Col lg={12}>
            <Form.Group>
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Details about the OD assignment"
              />
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Latitude</Form.Label>
              <Form.Control
                name="lat"
                type="number"
                step="any"
                value={formData.location?.lat ?? ""}
                onChange={handleChange}
                placeholder="e.g., 13.0827"
              />
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Longitude</Form.Label>
              <Form.Control
                name="lng"
                type="number"
                step="any"
                value={formData.location?.lng ?? ""}
                onChange={handleChange}
                placeholder="e.g., 80.2707"
              />
            </Form.Group>
          </Col>

          <Col lg={12}>
            <div className="d-flex gap-2 align-items-center">
              <Button variant="outline-secondary" onClick={fillCurrentLocation} disabled={locLoading}>
                {locLoading ? "Fetching location..." : "Get my location"}
              </Button>
              {renderError("location")}
              <small className="text-muted ms-2">Provide lat & lng or leave blank to set later</small>
            </div>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Status</Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="custom-select-arrow"
              >
                <option value="">Select Status</option>
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Cancelled">Cancelled</option>
              </Form.Select>
              {renderError("status")}
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>ID</Form.Label>
              <Form.Control
                type="text"
                name="id"
                value={formData.id}
                onChange={handleChange}
                placeholder="Auto or manual ID"
              />
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Created At</Form.Label>
              <Form.Control
                type="datetime-local"
                name="created_at"
                value={formData.created_at}
                onChange={handleChange}
                disabled
              />
            </Form.Group>
          </Col>

          <Col lg={6} md={12}>
            <Form.Group>
              <Form.Label>Updated At</Form.Label>
              <Form.Control
                type="datetime-local"
                name="updated_at"
                value={formData.updated_at}
                onChange={handleChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="form-actions mt-4 d-flex justify-content-end gap-2">
          <Button type="submit" className="btn-submit">Submit</Button>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>Cancel</Button>
        </div>
      </Form>
    </Container>
  );
};

export default AddOD;
